/* ==========================================================================
   Five Petal storefront — vanilla JS, no jQuery dependency.
   Modules: header scroll state, drawers (menu/cart), cart (Ajax API + Section
   Rendering API), product forms (variant → price/availability/media/url),
   quick add, tabs, gallery, hero video (reduced motion), theme-editor events.
   All hooks are data-fp-* attributes; see docs/FP-IMPLEMENTATION.md.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var body = doc.body;
  var theme = window.theme || {};
  var routes = theme.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update.js', root_url: '/' };
  var strings = (theme.fp && theme.fp.strings) || {};
  var settings = (theme.fp && theme.fp.settings) || {};
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false, addEventListener: function () {} };

  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function t(key, fallback, vars) {
    var s = strings[key] || fallback || '';
    if (vars) Object.keys(vars).forEach(function (k) { s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), vars[k]).replace('[' + k + ']', vars[k]); });
    return s;
  }

  /* Money -------------------------------------------------------------------- */
  function formatMoney(cents, format, noDecimals) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || theme.money_format || '${{amount}}';
    function addCommas(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    var match = formatString.match(placeholderRegex);
    var type = match ? match[1] : 'amount';
    if (noDecimals && cents % 100 === 0) type = type.replace('amount', 'amount_no_decimals').replace('amount_no_decimals_no_decimals', 'amount_no_decimals');
    switch (type) {
      case 'amount': value = addCommas(cents, 2); break;
      case 'amount_no_decimals': value = addCommas(cents, 0); break;
      case 'amount_with_comma_separator': value = addCommas(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = addCommas(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = addCommas(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = addCommas(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = addCommas(cents, 2, ' ', ','); break;
      default: value = addCommas(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* Header ------------------------------------------------------------------- */
  var header = {
    hero: null,
    solid: false,
    init: function () {
      root.classList.add('fp-scroll-pad');
      this.hero = qs('[data-fp-hero]');
      var hasHero = !!this.hero && !!qs('[data-fp-header]:not(.fp-header--never-overlay)');
      body.classList.toggle('fp-has-hero', hasHero);
      // Interior pages: no scroll logic at all — the header sits in normal flow and scrolls away (pass nine).
      if (!hasHero) { body.classList.remove('fp-header-solid'); return; }
      this.solid = body.classList.contains('fp-header-solid');
      this.update(true);
      if (!this.bound) {
        this.bound = true;
        var self = this;
        window.addEventListener('scroll', function () { self.update(); }, { passive: true });
        window.addEventListener('resize', function () { self.update(true); });
      }
    },
    // Read the variable from <body>: custom properties inherit, and the phone override (76px at ≤600px)
    // is set on body.fp-has-hero, so the root would still report the desktop/tablet value (V2 audit HM-2).
    expandedHeight: function () {
      var v = parseFloat(getComputedStyle(body).getPropertyValue('--fp-header-expanded'));
      return isNaN(v) ? 112 : v;
    },
    update: function (force) {
      if (!this.hero || !this.hero.isConnected) { this.hero = qs('[data-fp-hero]'); if (!this.hero) return; }
      var bottom = this.hero.getBoundingClientRect().bottom;
      // Fixed boundary (expanded height) plus an 8px re-entry buffer prevents oscillation.
      var overHero = bottom > this.expandedHeight() + (this.solid ? 8 : 0);
      var solid = !overHero;
      if (solid !== this.solid || force) {
        this.solid = solid;
        body.classList.toggle('fp-header-solid', solid);
      }
    },
    updateCount: function (count) {
      qsa('[data-fp-cart-count]').forEach(function (el) { el.textContent = count; });
      qsa('[data-fp-cart-label]').forEach(function (el) { el.setAttribute('aria-label', el.getAttribute('data-fp-cart-label').replace('[count]', count)); });
    }
  };

  /* Drawers ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var drawers = {
    open: null,
    opener: null,
    show: function (id, opener) {
      var el = doc.getElementById(id);
      if (!el) return;
      if (this.open && this.open !== el) this.hide(true);
      this.open = el;
      this.opener = opener || doc.activeElement;
      el.hidden = false;
      body.classList.add('fp-drawer-open');
      qsa('[data-fp-open="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var first = qs('.fp-drawer__close', el) || qs(FOCUSABLE, el);
      window.requestAnimationFrame(function () { if (first) first.focus(); });
      el.dispatchEvent(new CustomEvent('fp:drawer:open', { bubbles: true }));
    },
    hide: function (silent) {
      var el = this.open;
      if (!el) return;
      el.hidden = true;
      body.classList.remove('fp-drawer-open');
      qsa('[data-fp-open="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var opener = this.opener;
      this.open = null;
      this.opener = null;
      if (!silent && opener && opener.focus && opener.isConnected) opener.focus();
    },
    keydown: function (e) {
      if (!this.open) return;
      if (e.key === 'Escape') { e.preventDefault(); this.hide(); return; }
      if (e.key !== 'Tab') return;
      var items = qsa(FOCUSABLE, this.open).filter(function (n) { return n.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* Cart --------------------------------------------------------------------- */
  var cart = {
    busy: false,
    noteTimer: null,
    sectionIds: function () {
      var ids = ['fp-cart-drawer'];
      var page = qs('[data-fp-cart-page]');
      if (page && page.getAttribute('data-section-id')) ids.push(page.getAttribute('data-section-id'));
      return ids;
    },
    live: function (message) {
      var region = qs('[data-fp-cart-live]');
      if (!region) return;
      region.textContent = '';
      setTimeout(function () { region.textContent = message; }, 50);
    },
    setBusy: function (state) {
      this.busy = state;
      qsa('.fp-cart').forEach(function (el) { el.setAttribute('aria-busy', state ? 'true' : 'false'); });
    },
    applySections: function (sections) {
      if (!sections) return;
      var count = null;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var tmp = doc.createElement('div');
        tmp.innerHTML = html;
        var fresh = qs('[data-fp-cart-content]', tmp);
        if (!fresh) return;
        if (count === null) count = parseInt(fresh.getAttribute('data-item-count'), 10) || 0;
        var targets = id === 'fp-cart-drawer' ? qsa('#fp-cart-drawer [data-fp-cart-content]') : qsa('[data-section-id="' + id + '"] [data-fp-cart-content]');
        targets.forEach(function (target) { target.replaceWith(fresh.cloneNode(true)); });
        var desc = qs('[data-fp-cart-description]', tmp);
        var descTarget = qs('#fp-cart-drawer [data-fp-cart-description]');
        if (desc && descTarget) descTarget.textContent = desc.textContent;
      });
      if (count !== null) header.updateCount(count);
    },
    refresh: function () {
      var ids = this.sectionIds();
      var self = this;
      return fetch(window.location.pathname + '?sections=' + ids.join(','), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (sections) { self.applySections(sections); })
        .catch(function () {});
    },
    add: function (form) {
      if (this.busy) return;
      var self = this;
      var button = qs('[type="submit"]', form);
      var errorEl = qs('[data-fp-form-error]', form) || qs('[data-fp-form-error]', form.parentNode);
      var original = button ? button.innerHTML : '';
      var fd = new FormData(form);
      fd.append('sections', this.sectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('is-adding'); button.textContent = t('adding', 'Adding…'); }
      this.setBusy(true);
      fetch(routes.cart_add_url + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
        .then(function (res) {
          if (!res.ok) throw res.json;
          self.applySections(res.json.sections);
          var title = res.json.product_title || '';
          self.live(t('added_announcement', '[title] added to cart', { title: title }));
          doc.dispatchEvent(new CustomEvent('fp:cart:added', { detail: res.json }));
          if (button) { button.textContent = t('added', 'Added to cart'); }
          if (settings.openDrawerOnAdd !== false) drawers.show('fp-cart-drawer', button);
        })
        .catch(function (err) {
          var message = (err && (err.description || err.message)) || t('cart_error', 'Something went wrong. Please try again.');
          if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
          else if (window.console) console.warn('[fp] add to cart failed', err);
        })
        .then(function () {
          self.setBusy(false);
          if (button) {
            setTimeout(function () { button.innerHTML = original; button.removeAttribute('aria-disabled'); button.classList.remove('is-adding'); }, 1800);
          }
        });
    },
    change: function (line, quantity) {
      if (this.busy) return;
      var key = line.getAttribute('data-line-key');
      var errorEl = qs('[data-fp-line-error]', line);
      var input = qs('[data-fp-qty-input]', line);
      var self = this;
      this.setBusy(true);
      fetch(routes.cart_change_url + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
        .then(function (res) {
          if (!res.ok) throw res.json;
          self.applySections(res.json.sections);
          self.live(t('updated_announcement', 'Cart updated'));
          doc.dispatchEvent(new CustomEvent('fp:cart:changed', { detail: res.json }));
          // Cart quantity errors (e.g. stock) come back 200 with the unchanged cart; surface them.
          var fresh = qs('[data-line-key="' + key + '"] [data-fp-qty-input]');
          if (fresh && quantity > 0 && parseInt(fresh.value, 10) !== quantity) {
            var e = qs('[data-line-key="' + key + '"] [data-fp-line-error]');
            if (e) { e.textContent = t('stock_error', 'Only {{ count }} available.', { count: fresh.value }); e.hidden = false; }
          }
        })
        .catch(function (err) {
          if (input) input.value = input.getAttribute('data-initial');
          var message = (err && (err.description || err.message)) || t('cart_error', 'Something went wrong. Please try again.');
          if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
        })
        .then(function () { self.setBusy(false); });
    },
    note: function (value) {
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(function () {
        fetch(routes.cart_update_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: value }) }).catch(function () {});
      }, 400);
    }
  };

  /* Product form (variant selection) ------------------------------------------
     One instance per [data-fp-product-form] (sections/fp-main-product.liquid). Reads the slim product
     JSON the section prints, then on every variant / quantity change re-renders everything that
     depends on the selection, all found by data-fp-* hooks inside the closest [data-fp-product]:
       price + compare-at, "Available" / "Sold out", the submit label with the live line total,
       the quantity stepper (hidden when sold out), the notify-me block and its tag field, the
       gallery photo + caption, the per-cup price and the ?variant= URL. Nothing here talks to the
       cart; submission goes through the delegated [data-fp-product-form] submit handler below. */
  function ProductForm(el) {
    if (el.dataset.fpInit) return;
    el.dataset.fpInit = '1';
    // Product JSON: the section prints {id, tags, variants[{id, price, compare_at_price, available, options, featured_media}]}.
    var jsonEl = qs('[data-fp-product-json]', el.closest('[data-fp-product]') || el.parentNode) || qs('[data-fp-product-json]');
    var product = null;
    try { product = jsonEl ? JSON.parse(jsonEl.textContent) : null; } catch (e) { product = null; }
    var select = qs('[data-fp-variant-select]', el);
    var idInput = qs('input[name="id"]', el);
    var qty = qs('[name="quantity"]', el);
    var submit = qs('[data-fp-submit]', el);
    var total = qs('[data-fp-total]', el);
    var scope = el.closest('[data-fp-product]') || doc;
    var priceEl = qs('[data-fp-price]', scope);
    var compareEl = qs('[data-fp-compare]', scope);
    var availEl = qs('[data-fp-availability]', scope);
    var soldOutNotice = qs('[data-fp-sold-out-notice]', scope);
    // Notify-me form (snippets/fivepetal-oos-notify.liquid): shown for sold-out variants; its tag field
    // is rewritten so the recorded customer refers to the variant that was actually selected.
    var notify = qs('[data-fp-notify]', scope);
    var notifyTags = notify ? qs('[data-fp-notify-tags]', notify) : null;
    var notifyOptin = notify ? qs('[data-fp-notify-optin]', notify) : null;
    var notifyNewsletter = notify ? qs('[data-fp-notify-newsletter]', notify) : null;
    // Gallery hooks: data-fp-variant-media is "Option value::media id|…" from the merchant's mapping
    // (fpc.color_gallery seed), data-fp-general-media the photo that shows no single colour.
    var galleryEl = qs('[data-fp-gallery]', scope);
    var variantMedia = {};
    if (galleryEl && galleryEl.getAttribute('data-fp-variant-media')) {
      galleryEl.getAttribute('data-fp-variant-media').split('|').forEach(function (pair) {
        var kv = pair.split('::'); if (kv[0] && kv[1]) variantMedia[kv[0].trim()] = kv[1].trim();
      });
    }
    var generalMedia = galleryEl ? galleryEl.getAttribute('data-fp-general-media') : '';
    var caption = galleryEl ? qs('[data-fp-media-caption]', galleryEl) : null;
    var captionTemplate = galleryEl ? galleryEl.getAttribute('data-fp-caption-template') : '';
    var cupEl = qs('[data-fp-cup-price]', scope);
    var cupValue = cupEl ? qs('[data-fp-cup-value]', cupEl) : null;
    // Products tagged no-decimals (the coffees) show "$42", matching the price snippet's server output.
    var noDecimals = product && Array.isArray(product.tags) && product.tags.some(function (tag) { return /no[-_]decimals/i.test(tag); });

    // The selected variant object, from the <select> or, for single-variant products, the hidden id input.
    function currentVariant() {
      if (!product) return null;
      var id = select ? select.value : (idInput ? idInput.value : null);
      for (var i = 0; i < product.variants.length; i++) if (String(product.variants[i].id) === String(id)) return product.variants[i];
      return null;
    }
    // Re-render every selection-dependent element. Cheap enough to run on each change without diffing.
    // opts.keepMedia: a quantity change must not touch the gallery, or a photograph the customer chose
    // by thumbnail would snap back to the variant/fallback image (V2 audit item 8).
    function render(opts) {
      var keepMedia = !!(opts && opts.keepMedia);
      var v = currentVariant();
      var q = qty ? Math.max(1, parseInt(qty.value, 10) || 1) : 1;
      if (idInput && select) idInput.value = select.value;
      if (!v) { // unknown id (e.g. a stale ?variant=): disable rather than guess
        if (submit) { submit.disabled = true; submit.textContent = t('unavailable', 'Unavailable'); }
        if (availEl) availEl.textContent = t('unavailable', 'Unavailable');
        return;
      }
      if (priceEl) priceEl.textContent = formatMoney(v.price, theme.money_format, noDecimals);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) { compareEl.textContent = formatMoney(v.compare_at_price, theme.money_format, noDecimals); compareEl.hidden = false; }
        else compareEl.hidden = true;
      }
      if (availEl) availEl.textContent = v.available ? t('available', 'Available') : t('sold_out', 'Sold out');
      // Submit button: "Add to cart  $84" (label × quantity) while available, disabled "Sold out" otherwise.
      // The label lives in data-fp-label so the total span can be rebuilt without losing the translation.
      if (submit) {
        submit.disabled = !v.available;
        if (!v.available) submit.textContent = t('sold_out', 'Sold out');
        else {
          var label = submit.getAttribute('data-fp-label') || t('add_to_cart', 'Add to cart');
          submit.innerHTML = label + (total || submit.hasAttribute('data-fp-show-total') ? ' <span data-fp-total>' + formatMoney(v.price * q, theme.money_format, noDecimals) + '</span>' : '');
          total = qs('[data-fp-total]', submit);
        }
      }
      qsa('[data-fp-quantity-wrap]', el).forEach(function (w) { w.hidden = !v.available; }); // no stepper for a sold-out variant
      qsa('[data-fp-shipping-notice]', el).forEach(function (n) { n.hidden = !v.available; }); // sold out keeps its own message
      if (soldOutNotice) soldOutNotice.hidden = v.available;
      // Notify-me: visible only for the sold-out variant; the customer tags record which variant was wanted.
      if (notify) {
        notify.hidden = v.available;
        if (notifyTags) {
          notifyTags.value = [notify.getAttribute('data-fp-notify-base-tags'), 'product-' + notify.getAttribute('data-fp-notify-product'), 'product_handle-' + notify.getAttribute('data-fp-notify-handle'), 'variant-' + v.id].join(',');
        }
      }
      // Photograph (initial render and colour changes only): Shopify's variant media wins; else the
      // merchant's option → gallery map; else the explicitly general photo, so a colour without its own
      // picture never shows another colour.
      if (!keepMedia) {
        var assigned = !!(v.featured_media && v.featured_media.id);
        var mediaId = assigned ? String(v.featured_media.id) : null;
        if (!mediaId && v.options) v.options.forEach(function (o) { if (!mediaId && variantMedia[o]) mediaId = variantMedia[o]; });
        var mapped = !!mediaId;
        if (!mediaId && generalMedia) mediaId = generalMedia;
        if (mediaId) gallery.show(scope, mediaId);
        // Caption: when the photo is not the selected colour's own picture, say so with the selected value.
        if (caption) {
          var showCaption = !assigned && !mapped && product.variants.length > 1;
          caption.hidden = !showCaption;
          if (showCaption && captionTemplate) caption.textContent = captionTemplate.replace('[value]', v.title);
        }
      }
      // Per-cup price: active variant price × dose ÷ net coffee grams (never the shipping weight).
      if (cupEl && cupValue) {
        var grams = parseFloat(cupEl.getAttribute('data-fp-grams')), dose = parseFloat(cupEl.getAttribute('data-fp-dose')) || 15;
        if (grams > 0 && v.price > 0) {
          cupValue.textContent = (cupEl.getAttribute('data-fp-cup-template') || '[price]').replace('[price]', formatMoney(Math.round(v.price * dose / grams), theme.money_format, false));
          cupEl.hidden = false;
        } else cupEl.hidden = true;
      }
      // Keep ?variant= in the address bar so a shared or reloaded URL restores the same colour/size.
      if (el.getAttribute('data-fp-history') === 'true' && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    if (select) select.addEventListener('change', render);
    if (notifyOptin && notifyNewsletter) {
      // The hidden "newsletter" field grants marketing consent; only send it when the box is ticked.
      var syncOptin = function () { notifyNewsletter.disabled = !notifyOptin.checked; };
      notifyOptin.addEventListener('change', syncOptin);
      syncOptin();
    }
    var notifyForm = notify ? qs('[data-fp-notify-form]', notify) : null;
    if (notifyForm) {
      // Native validation blocks submission for an empty/invalid email; mirror it with an inline message.
      var notifyEmail = qs('input[type="email"]', notifyForm);
      if (notifyEmail) {
        notifyEmail.addEventListener('invalid', function () {
          var err = qs('[data-fp-form-error]', notifyForm);
          if (err) { err.textContent = t('notify_error', 'Please enter a valid email address.'); err.hidden = false; }
          notifyEmail.setAttribute('aria-invalid', 'true');
        });
        notifyEmail.addEventListener('input', function () {
          var err = qs('[data-fp-form-error]', notifyForm);
          if (notifyEmail.checkValidity()) { if (err) err.hidden = true; notifyEmail.removeAttribute('aria-invalid'); }
        });
      }
      notifyForm.addEventListener('submit', function (e) {
        // Native validation runs first (required + type=email). Show the inline message if it fails.
        var email = qs('input[type="email"]', notifyForm);
        var err = qs('[data-fp-form-error]', notifyForm);
        if (email && !email.checkValidity()) {
          e.preventDefault();
          if (err) { err.textContent = t('notify_error', 'Please enter a valid email address.'); err.hidden = false; }
          email.focus();
          return;
        }
        if (err) err.hidden = true;
      });
    }
    // Quantity: clamp to the input's min/max (max = settings.fp_max_quantity), grey out the stepper
    // button that can go no further, then refresh the live total. The +/- buttons themselves are
    // handled by the delegated [data-fp-qty] click handler, which ends by dispatching this change event.
    if (qty) qty.addEventListener('change', function () {
      var max = parseInt(qty.getAttribute('max'), 10) || 99, min = parseInt(qty.getAttribute('min'), 10) || 1;
      var v = parseInt(qty.value, 10);
      if (isNaN(v) || v < min) v = min;
      if (v > max) v = max;
      qty.value = v;
      qsa('[data-fp-qty]', el).forEach(function (b) { b.disabled = (b.getAttribute('data-fp-qty') === '-1' && v <= min) || (b.getAttribute('data-fp-qty') === '1' && v >= max); });
      render({ keepMedia: true });
    });
    render();
  }

  /* Gallery ---------------------------------------------------------------------
     Two markups share the data-fp-gallery / data-fp-slide / data-fp-thumb hooks:
       • gear: one slide visible at a time (`hidden` toggled), thumbnails are aria-pressed buttons;
       • coffee (Sept 2026): a horizontal scroll-snap track (data-fp-track). Touch swipes it natively;
         thumbnails (anchor links, aria-current), a mouse drag, and ←/→ Home/End on the focused track scroll
         it; the
         scroll position is mirrored into the caption, the "n / total" counter and the selected thumbnail.
         Nothing loops or auto-advances; smooth scrolling is dropped under prefers-reduced-motion.
     gallery.show() serves both, so ProductForm's variant → media sync is unchanged. Quantity changes call
     render({ keepMedia: true }) and cart updates never touch the product section, so a chosen photo stays. */
  var gallery = {
    show: function (scope, mediaId) {
      var g = qs('[data-fp-gallery]', scope);
      if (!g) return;
      var slides = qsa('[data-fp-slide]', g);
      var target = slides.filter(function (s) { return s.getAttribute('data-media-id') === String(mediaId); })[0];
      if (!target) return;
      if (qs('[data-fp-track]', g)) { gallery.goTo(g, slides.indexOf(target)); return; }
      slides.forEach(function (s) { s.hidden = s !== target; });
      gallery.mark(g, String(mediaId));
    },
    // Selected-state attribute per thumbnail kind (button → aria-pressed, link → aria-current).
    mark: function (g, mediaId) {
      qsa('[data-fp-thumb]', g).forEach(function (b) {
        var on = b.getAttribute('data-media-id') === mediaId;
        b.setAttribute(b.tagName === 'A' ? 'aria-current' : 'aria-pressed', on ? 'true' : 'false');
      });
    },
    goTo: function (g, index, instant) {
      var track = qs('[data-fp-track]', g), slides = qsa('[data-fp-slide]', g);
      if (!track || !slides.length) return;
      index = Math.max(0, Math.min(slides.length - 1, index));
      var left = slides[index].offsetLeft;
      var smooth = !instant && !reducedMotion.matches;
      // Ignore the intermediate positions a smooth scroll passes through; sync() runs once it lands.
      g._fpTarget = left; g._fpLockUntil = Date.now() + (smooth ? 1200 : 0);
      if (track.scrollTo) track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' }); else track.scrollLeft = left;
      gallery.sync(g, index);
    },
    // Reflect slide `index` in the thumbnails, caption, counter; preload its neighbours.
    sync: function (g, index) {
      var slides = qsa('[data-fp-slide]', g);
      if (!slides[index] || g._fpIndex === index) return;
      g._fpIndex = index;
      var slide = slides[index];
      qsa('[data-fp-thumb]', g).forEach(function (b, i) { b.setAttribute('aria-current', i === index ? 'true' : 'false'); });
      var caption = qs('[data-fp-gallery-caption]', g);
      if (caption) caption.textContent = slide.getAttribute('data-fp-caption') || '';
      var count = qs('[data-fp-gallery-count]', g);
      if (count) count.textContent = (g.getAttribute('data-fp-count-template') || '[n] / ' + slides.length).replace('[n]', index + 1);
      // Keep the selected thumbnail in view by scrolling only the rail, never the page.
      var thumb = qsa('[data-fp-thumb]', g)[index], rail = thumb && thumb.parentNode;
      if (thumb && rail && rail.scrollWidth > rail.clientWidth) {
        var start = thumb.offsetLeft - rail.offsetLeft, end = start + thumb.offsetWidth;
        if (start < rail.scrollLeft) rail.scrollLeft = start - 4;
        else if (end > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = end - rail.clientWidth + 4;
      }
      // Later photographs are lazy; fetch the current one and its neighbours now so a swipe lands on a picture.
      [index - 1, index, index + 1].forEach(function (i) {
        var img = slides[i] && qs('img[loading="lazy"]', slides[i]);
        if (img) img.loading = 'eager';
      });
    },
    init: function (g) {
      if (g.dataset.fpGalleryInit) return;
      g.dataset.fpGalleryInit = '1';
      var track = qs('[data-fp-track]', g);
      if (!track) { // gear: honour the server-rendered visible slide
        var first = qs('[data-fp-slide]:not([hidden])', g);
        if (first) gallery.show(g.closest('[data-fp-product]') || doc, first.getAttribute('data-media-id'));
        return;
      }
      var slides = qsa('[data-fp-slide]', g);
      var raf = null;
      function fromScroll() {
        raf = null;
        if (g._fpLockUntil && Date.now() < g._fpLockUntil && Math.abs(track.scrollLeft - g._fpTarget) > 2) return;
        g._fpLockUntil = 0;
        var w = track.clientWidth || 1;
        gallery.sync(g, Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w))));
      }
      track.addEventListener('scroll', function () { if (raf === null) raf = requestAnimationFrame(fromScroll); }, { passive: true });
      track.addEventListener('keydown', function (e) {
        var i = g._fpIndex || 0, next = null;
        if (e.key === 'ArrowRight') next = i + 1;
        else if (e.key === 'ArrowLeft') next = i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = slides.length - 1;
        if (next === null || next === i || next < 0 || next >= slides.length) { if (next !== null) e.preventDefault(); return; }
        e.preventDefault();
        gallery.goTo(g, next);
      });
      // Mouse drag = swipe. Touch and pen keep the browser's native scroll-snap swipe; a mouse gets the same
      // gesture: press, drag the track, release, and it settles on the nearest slide (or the next one
      // when the drag was decisive). A short press with no movement is left alone.
      var drag = null;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag = { x: e.clientX, left: track.scrollLeft, moved: false, id: e.pointerId };
      });
      track.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 4) return;
        if (!drag.moved) { drag.moved = true; track.classList.add('is-dragging'); try { track.setPointerCapture(drag.id); } catch (err) {} }
        track.scrollLeft = drag.left - dx;
        e.preventDefault();
      });
      function endDrag(e) {
        if (!drag || (e && e.pointerId !== drag.id)) return;
        var d = drag; drag = null;
        if (!d.moved) return;
        track.classList.remove('is-dragging');
        var w = track.clientWidth || 1, dx = e ? e.clientX - d.x : 0;
        var from = Math.round(d.left / w), target = Math.round(track.scrollLeft / w);
        if (target === from && Math.abs(dx) > w * 0.12) target = from - Math.sign(dx); // decisive short drag → next/previous
        gallery.goTo(g, target);
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', function () { if (drag && drag.moved) endDrag(); });
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      // A resize changes the slide width: re-snap to the current slide without animation.
      window.addEventListener('resize', function () { if (g.isConnected) gallery.goTo(g, g._fpIndex || 0, true); });
      g._fpIndex = -1;
      gallery.sync(g, Math.round(track.scrollLeft / (track.clientWidth || 1)) || 0);
    }
  };

  /* Tabs ---------------------------------------------------------------------- */
  function initTabs(container) {
    if (container.dataset.fpInit) return;
    container.dataset.fpInit = '1';
    var tabs = qsa('[role="tab"]', container);
    function activate(tab, focus) {
      tabs.forEach(function (x) {
        var on = x === tab;
        x.setAttribute('aria-selected', on ? 'true' : 'false');
        x.tabIndex = on ? 0 : -1;
        var panel = doc.getElementById(x.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(tab); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) { e.preventDefault(); activate(next, true); }
      });
    });
    var selected = tabs.filter(function (x) { return x.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
    if (selected) activate(selected);
  }

  /* Hero video ------------------------------------------------------------------
     Ported from the reference hero-media component: the poster is always present; the video has no
     autoplay attribute and no controls. We call muted inline play() ourselves, reveal the video only
     once "playing" fires, hide it immediately on pause/waiting/emptied/ended/error, retry after
     visibility returns, pageshow, and one ordinary user interaction. Reduced motion never loads it.
     A blocked movie therefore leaves the photograph, never a native player button.
     "stalled" is deliberately NOT a hide trigger: it only means the fetch paused, and buffered playback
     usually continues; hiding on it covered a playing movie with the poster (V2 audit HM-1). A real
     interruption still fires "waiting", "pause" or "error", which do hide it. */
  var compactQuery = window.matchMedia ? window.matchMedia('(max-width: 600px)') : { matches: false, addEventListener: function () {} };
  function initHeroVideo(video) {
    if (video.dataset.fpInit) return;
    video.dataset.fpInit = '1';
    var active = true;
    var hide = function () { if (active) video.setAttribute('data-playing', 'false'); };
    var reveal = function () { if (active) video.setAttribute('data-playing', 'true'); };
    function source() {
      var mobile = compactQuery.matches;
      var src = mobile ? (video.getAttribute('data-src-mobile') || video.getAttribute('data-src-desktop')) : video.getAttribute('data-src-desktop');
      var poster = mobile ? (video.getAttribute('data-poster-mobile') || video.getAttribute('data-poster-desktop')) : video.getAttribute('data-poster-desktop');
      if (video.getAttribute('src') !== src) { hide(); video.setAttribute('src', src); if (poster) video.setAttribute('poster', poster); video.load(); }
    }
    function attempt() {
      if (!active || reducedMotion.matches || doc.visibilityState === 'hidden' || !video.paused) return;
      video.defaultMuted = true; video.muted = true; video.playsInline = true; video.controls = false;
      var p = video.play();
      if (p && p.catch) p.catch(function () { if (video.paused) hide(); });
    }
    function apply() {
      if (reducedMotion.matches) { video.pause(); hide(); video.hidden = true; video.removeAttribute('src'); return; }
      video.hidden = false; source(); attempt();
    }
    video.addEventListener('playing', reveal);
    ['pause', 'waiting', 'emptied', 'ended', 'error'].forEach(function (e) { video.addEventListener(e, hide); });
    video.addEventListener('canplay', attempt);
    doc.addEventListener('visibilitychange', function () { if (doc.visibilityState === 'hidden') { hide(); video.pause(); } else attempt(); });
    window.addEventListener('pageshow', attempt);
    window.addEventListener('pointerdown', attempt, { once: true, passive: true });
    window.addEventListener('keydown', attempt, { once: true });
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', apply);
    if (compactQuery.addEventListener) compactQuery.addEventListener('change', apply);
    // Start after the load event: the poster is already painted, and the ~2 MB loop no longer competes with
    // stylesheets, fonts and product photos for the first paint (perf pass, 11 Sept 2026).
    if (doc.readyState === 'complete') apply(); else window.addEventListener('load', apply, { once: true });
  }

  /* Init ------------------------------------------------------------------------ */
  function init(scope) {
    scope = scope || doc;
    header.init();
    qsa('[data-fp-product-form]', scope).forEach(ProductForm);
    qsa('[data-fp-tabs]', scope).forEach(initTabs);
    qsa('[data-fp-hero-video]', scope).forEach(initHeroVideo);
    qsa('[data-fp-gallery]', scope).forEach(gallery.init);
  }

  /* Delegated events ------------------------------------------------------------- */
  doc.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-fp-open]');
    if (opener) {
      var id = opener.getAttribute('data-fp-open');
      if (doc.getElementById(id)) { e.preventDefault(); drawers.show(id, opener); }
      return;
    }
    if (e.target.closest('[data-fp-close]') || e.target.closest('[data-fp-close-on-nav]')) {
      if (e.target.closest('[data-fp-drawer]')) { if (e.target.closest('[data-fp-close]') && !e.target.closest('a[href]')) e.preventDefault(); drawers.hide(); }
      return;
    }
    var qtyBtn = e.target.closest('[data-fp-qty]');
    if (qtyBtn) {
      e.preventDefault();
      var wrap = qtyBtn.closest('.fp-quantity');
      var input = qs('input', wrap);
      var delta = parseInt(qtyBtn.getAttribute('data-fp-qty'), 10);
      var max = parseInt(input.getAttribute('max'), 10) || 99, min = parseInt(input.getAttribute('min'), 10) || 1;
      var next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + delta));
      if (next === parseInt(input.value, 10)) return;
      input.value = next;
      var line = qtyBtn.closest('[data-fp-line]');
      if (line) cart.change(line, next);
      else input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    var remove = e.target.closest('[data-fp-remove]');
    if (remove) { e.preventDefault(); cart.change(remove.closest('[data-fp-line]'), 0); return; }
    var thumb = e.target.closest('[data-fp-thumb]');
    if (thumb) {
      e.preventDefault();
      var tg = thumb.closest('[data-fp-gallery]');
      if (tg && qs('[data-fp-track]', tg)) gallery.goTo(tg, qsa('[data-fp-thumb]', tg).indexOf(thumb)); // by position: media ids can repeat
      else gallery.show(thumb.closest('[data-fp-product]') || doc, thumb.getAttribute('data-media-id'));
    }
  });

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-fp-add-form], [data-fp-product-form]');
    if (!form) return;
    e.preventDefault();
    cart.add(form);
  });

  doc.addEventListener('change', function (e) {
    var input = e.target.closest('[data-fp-qty-input]');
    if (input) {
      var line = input.closest('[data-fp-line]');
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var v = Math.min(max, Math.max(1, parseInt(input.value, 10) || 1));
      input.value = v;
      if (line) cart.change(line, v);
    }
  });
  doc.addEventListener('input', function (e) {
    var note = e.target.closest('[data-fp-cart-note]');
    if (note) cart.note(note.value);
  });
  doc.addEventListener('keydown', function (e) { drawers.keydown(e); });

  // Vendor cart updates (legacy forms/quick-buy) → keep the drawer and count in sync.
  doc.addEventListener('theme:cartchanged', function () { cart.refresh(); });

  // Theme editor lifecycle
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
  doc.addEventListener('shopify:section:select', function (e) {
    if (qs('[data-fp-cart-drawer]', e.target)) drawers.show('fp-cart-drawer');
  });
  doc.addEventListener('shopify:section:deselect', function (e) {
    if (qs('[data-fp-cart-drawer]', e.target)) drawers.hide(true);
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  window.FivePetal = { formatMoney: formatMoney, cart: cart, drawers: drawers, init: init };
})();
